import { useState, useEffect } from 'react';
import { Scanner } from '@yudiel/react-qr-scanner';
import { X, Keyboard, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';

const BarcodeScanner = ({ onScan, onClose }) => {
  const [error, setError] = useState(null);
  const [hasPermission, setHasPermission] = useState(null);

  useEffect(() => {
    // Check camera permission (requires secure context)
    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
      setHasPermission(false);
      setError('Camera access requires HTTPS (or localhost). Use manual entry or switch to a secure URL.');
      return;
    }

    navigator.mediaDevices.getUserMedia({ video: true })
      .then(() => setHasPermission(true))
      .catch(() => {
        setHasPermission(false);
        setError('Camera access denied. Please allow camera access to scan barcodes.');
      });
  }, []);

  const handleScan = (result) => {
    if (result && result.length > 0) {
      const code = result[0].rawValue;
      if (code) {
        onScan(code);
      }
    }
  };

  const handleError = (err) => {
    console.error('Scanner error:', err);
    setError('Failed to access camera. Try manual entry instead.');
  };

  return (
    <div className="scanner-overlay" data-testid="barcode-scanner">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-10 p-4 bg-gradient-to-b from-black/50 to-transparent">
        <div className="flex items-center justify-between">
          <h2 className="text-white font-semibold">Scan Barcode</h2>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center"
            data-testid="close-scanner-btn"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>
      </div>

      {/* Scanner */}
      {hasPermission && !error && (
        <div className="h-full">
          <Scanner
            onScan={handleScan}
            onError={handleError}
            formats={['ean_13', 'ean_8', 'code_128', 'code_39', 'qr_code', 'upc_a', 'upc_e']}
            components={{
              audio: false,
              torch: true,
            }}
            styles={{
              container: { height: '100%' },
              video: { objectFit: 'cover' },
            }}
          />
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="flex flex-col items-center justify-center h-full p-6 text-center">
          <AlertCircle className="w-16 h-16 text-white/50 mb-4" />
          <p className="text-white mb-6">{error}</p>
          <Button
            onClick={onClose}
            variant="secondary"
            className="w-full max-w-xs"
            data-testid="scanner-fallback-btn"
          >
            <Keyboard className="w-5 h-5 mr-2" />
            Use Manual Entry
          </Button>
        </div>
      )}

      {/* Loading */}
      {hasPermission === null && !error && (
        <div className="flex items-center justify-center h-full">
          <div className="text-white">Requesting camera access...</div>
        </div>
      )}

      {/* Scan Guide */}
      {hasPermission && !error && (
        <div className="absolute bottom-0 left-0 right-0 z-10 p-6 bg-gradient-to-t from-black/50 to-transparent">
          <p className="text-white text-center text-sm">
            Point camera at barcode
          </p>
        </div>
      )}
    </div>
  );
};

export default BarcodeScanner;
